import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PiArrowsDownUp, PiCaretDown, PiFunnel, PiMagnifyingGlass, PiPlus, PiSortAscending } from 'react-icons/pi';
import {
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  compareFinancialRecordsByDate,
  getAmountCents,
  getFinancialDate,
  parseCivilDate,
} from '../../domain/finance';
import TransactionItem from '../TransactionItem';
import SortSheet from '../SortSheet';
import './styles.css';

const QUICK_FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'income', label: 'Receitas' },
  { key: 'expense', label: 'Despesas' },
  { key: 'pending', label: 'Pendentes' },
];

const getInitialSort = (defaultDirection) => {
  if (defaultDirection) return { key: 'data', direction: defaultDirection };
  try {
    const stored = JSON.parse(localStorage.getItem('sortConfig'));
    if (['data', 'nome', 'valor'].includes(stored?.key) && ['asc', 'desc'].includes(stored?.direction)) return stored;
  } catch (error) {
    // Preferência inválida: mantém o comportamento legado padrão.
  }
  return { key: 'data', direction: 'asc' };
};

const sortTransactions = (items, config) => {
  const direction = config.direction === 'asc' ? 1 : -1;
  return [...items].sort((left, right) => {
    if (config.key === 'nome') return direction * String(left.nome || '').localeCompare(String(right.nome || ''));
    if (config.key === 'valor') {
      const leftAmount = getAmountCents(left);
      const rightAmount = getAmountCents(right);
      if (leftAmount === null && rightAmount === null) return 0;
      if (leftAmount === null) return 1;
      if (rightAmount === null) return -1;
      return direction * (leftAmount - rightAmount);
    }
    const comparison = compareFinancialRecordsByDate(left, right);
    return direction * (comparison ?? 0);
  });
};

const TransactionsSection = ({
  receitas,
  despesas,
  showValues,
  searchQuery,
  onSearchChange,
  onOpenFilters,
  filtersActive,
  onClearFilters,
  onNew,
  onEdit,
  onDelete,
  onShare,
  onChangeAttribution,
  onToggleStatus,
  hasPeriodTransactions,
  contextDescription = 'Receitas e despesas do período selecionado.',
  onOpenVirtual,
  quickFilter: controlledQuickFilter,
  onQuickFilterChange,
  defaultSortDirection,
}) => {
  const [internalQuickFilter, setInternalQuickFilter] = useState('all');
  const quickFilter = controlledQuickFilter ?? internalQuickFilter;
  const setQuickFilter = onQuickFilterChange || setInternalQuickFilter;
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [sortConfig, setSortConfig] = useState(() => getInitialSort(defaultSortDirection));

  const transactions = useMemo(() => [
    ...receitas.map((item) => ({ ...item, tipo: TRANSACTION_TYPES.INCOME })),
    ...despesas.map((item) => ({ ...item, tipo: TRANSACTION_TYPES.EXPENSE })),
  ], [receitas, despesas]);

  const visibleTransactions = useMemo(() => {
    const filtered = transactions.filter((item) => {
      if (quickFilter === 'income') return item.tipo === TRANSACTION_TYPES.INCOME;
      if (quickFilter === 'expense') return item.tipo === TRANSACTION_TYPES.EXPENSE;
      if (quickFilter === 'pending') return item.status === TRANSACTION_STATUS.PENDING;
      return true;
    });
    return sortTransactions(filtered, sortConfig);
  }, [transactions, quickFilter, sortConfig]);

  const updateSort = (value) => {
    const [key, direction] = value.split('-');
    const next = { key, direction };
    setSortConfig(next);
    localStorage.setItem('sortConfig', JSON.stringify(next));
  };

  const clearFilters = () => {
    setQuickFilter('all');
    onSearchChange('');
    if (filtersActive) onClearFilters();
  };



  const filteredEmpty = visibleTransactions.length === 0;

  return (
    <section id="movimentacoes" className="transactions-section" aria-labelledby="transactions-title">
      <div className="transactions-section__heading">
        <div>
          <div className="transactions-section__title-row">
            <h2 id="transactions-title">Movimentações</h2>
            <span aria-label={`${transactions.length} movimentações`}>{transactions.length}</span>
          </div>
          <p>{contextDescription}</p>
        </div>
      </div>

      <div className="transactions-section__toolbar">
        <label className="transactions-section__search">
          <span className="sr-only">Pesquisar movimentações</span>
          <PiMagnifyingGlass aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Pesquisar movimentações"
          />
        </label>
        <div className="transactions-section__toolbar-actions">
          <button
            type="button"
            className={`transactions-section__filter-button${filtersActive ? ' is-active' : ''}`}
            onClick={onOpenFilters}
            aria-label={filtersActive ? 'Abrir filtros avançados, filtros ativos' : 'Abrir filtros avançados'}
          >
            <PiFunnel aria-hidden="true" />
            <span>Filtros</span>
          </button>
          <button
            type="button"
            className="transactions-section__sort-button"
            onClick={() => setShowSortSheet(true)}
            aria-label="Abrir opções de ordenação"
          >
            <PiSortAscending aria-hidden="true" />
            <span>Ordenar</span>
          </button>
        </div>
        <label className="transactions-section__sort">
          <span className="sr-only">Ordenar movimentações</span>
          <select value={`${sortConfig.key}-${sortConfig.direction}`} onChange={(event) => updateSort(event.target.value)} aria-label="Ordenar movimentações">
            <option value="data-asc">Data: antigas primeiro</option>
            <option value="data-desc">Data: recentes primeiro</option>
            <option value="nome-asc">Nome: A–Z</option>
            <option value="nome-desc">Nome: Z–A</option>
            <option value="valor-desc">Maior valor</option>
            <option value="valor-asc">Menor valor</option>
          </select>
          <PiCaretDown className="transactions-section__sort-icon" size={18} aria-hidden="true" data-testid="sort-select-chevron" />
        </label>
      </div>

      <div className="transactions-section__quick-filters" aria-label="Filtros rápidos">
        {QUICK_FILTERS.map((filter) => (
          <button
            type="button"
            key={filter.key}
            className={quickFilter === filter.key ? 'is-selected' : ''}
            aria-pressed={quickFilter === filter.key}
            onClick={() => setQuickFilter(filter.key)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {showSortSheet && (
        <SortSheet
          value={`${sortConfig.key}-${sortConfig.direction}`}
          onClose={() => setShowSortSheet(false)}
          onSelect={(value) => {
            updateSort(value);
            setShowSortSheet(false);
          }}
        />
      )}

      {filteredEmpty ? (
        <div className="transactions-section__empty" role="status">
          <div className="transactions-section__empty-icon" aria-hidden="true">
            <PiArrowsDownUp />
          </div>
          {!hasPeriodTransactions && !filtersActive && !searchQuery ? (
            <>
              <h3>Nenhuma movimentação neste mês</h3>
              <p>Adicione uma receita ou despesa para começar.</p>
              <button type="button" className="transactions-section__empty-primary" onClick={onNew}><PiPlus aria-hidden="true" />Nova movimentação</button>
            </>
          ) : (
            <>
              <h3>Nenhuma movimentação encontrada</h3>
              <p>Ajuste a pesquisa ou os filtros atuais.</p>
              <button type="button" className="transactions-section__empty-secondary" onClick={clearFilters}>Limpar filtros</button>
            </>
          )}
        </div>
      ) : (
        <ul className="transactions-section__list">
          {visibleTransactions.map((item, index) => {
            const year = parseCivilDate(getFinancialDate(item))?.year;
            const previousYear = index > 0
              ? parseCivilDate(getFinancialDate(visibleTransactions[index - 1]))?.year
              : null;
            return (
              <React.Fragment key={`${item.source || item.tipo}-${item.id}`}>
                {year && previousYear && year !== previousYear && (
                  <li className="transactions-section__year-separator" role="presentation">
                    <h3 aria-label={`Movimentações de ${year}`}>{year}</h3>
                  </li>
                )}
                <TransactionItem
                  item={item}
                  showValues={showValues}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onShare={onShare}
                  onChangeAttribution={onChangeAttribution}
                  onToggleStatus={onToggleStatus}
                  onOpenVirtual={onOpenVirtual}
                />
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </section>
  );
};

TransactionsSection.propTypes = {
  receitas: PropTypes.array.isRequired,
  despesas: PropTypes.array.isRequired,
  showValues: PropTypes.bool,
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onOpenFilters: PropTypes.func.isRequired,
  filtersActive: PropTypes.bool.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onShare: PropTypes.func,
  onChangeAttribution: PropTypes.func,
  onToggleStatus: PropTypes.func.isRequired,
  hasPeriodTransactions: PropTypes.bool.isRequired,
  contextDescription: PropTypes.string,
  onOpenVirtual: PropTypes.func,
  quickFilter: PropTypes.oneOf(['all', 'income', 'expense', 'pending']),
  onQuickFilterChange: PropTypes.func,
  defaultSortDirection: PropTypes.oneOf(['asc', 'desc']),
};

export default TransactionsSection;
